import { useState } from "react";
import {
  useCreateStripeSession,
  useCompleteCheckout,
} from "../basket-api";
import {
  handleApiError,
  showErrorToast,
} from "../error-handling";
import { buildCheckoutBody } from "../checkout-utils";
import type { StripeSessionResponse } from "../types";

/**
 * Hook for handling checkout operations (both free and paid checkouts)
 */
export function useBasketCheckout(checkoutId: string | null) {
  const [isProcessing, setIsProcessing] = useState(false);
  const createStripeSession = useCreateStripeSession();
  const completeCheckout = useCompleteCheckout();

  const handleProceedToCheckout = async (
    total: number,
    bookingEmailAddress: string,
    marketingConsent: boolean,
  ) => {
    if (!checkoutId) return;

    // For £0 checkouts, complete checkout directly
    if (total === 0) {
      setIsProcessing(true);
      try {
        const body = buildCheckoutBody(bookingEmailAddress, marketingConsent);
        await completeCheckout.mutateAsync({
          params: { path: { checkoutId } },
          ...(body ? { body } : {}),
        });
        window.location.href = `/checkout/success?checkoutId=${checkoutId}`;
      } catch (error) {
        const apiError = handleApiError(error);
        showErrorToast(apiError);
        setIsProcessing(false);
      }
      return;
    }

    // For paid checkouts, create Stripe session
    setIsProcessing(true);
    try {
      const body = buildCheckoutBody(bookingEmailAddress, marketingConsent);
      const result = (await createStripeSession.mutateAsync({
        params: { path: { checkoutId } },
        ...(body ? { body } : {}),
      })) as StripeSessionResponse;

      // Check if payment is already completed
      const redirectToSuccess = (result as { redirectToSuccess?: boolean })
        .redirectToSuccess;
      if (redirectToSuccess && result.sessionUrl) {
        // Payment already completed - redirect to success page
        window.location.href = result.sessionUrl;
      } else if (result.sessionUrl) {
        // Normal flow - redirect to Stripe checkout
        window.location.href = result.sessionUrl;
      } else {
        showErrorToast({ message: "No checkout session URL received" });
      }
    } catch (error) {
      const apiError = handleApiError(error);
      showErrorToast(apiError);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    handleProceedToCheckout,
    isProcessing,
  };
}







